/**
 * DeFiLlamaService module.
 *
 * This module provides functions for interacting with the DeFi Llama API.
 * It includes functions to fetch yield data for native assets.
 *
 * @module DeFiLlamaService
 */

import { logInfo, logError } from '@/utils/logger'; // Import the logger utility for logging
import APP_CONFIG from '@/config/app'; // Import the app config

const generateStorageKey = (url: string, type: string) => `${type}_${url}`;

const isLocalStorageValid = (
  timestampKey: string,
  timeThreshold: number,
): boolean => {
  const storedTimestamp = localStorage.getItem(timestampKey);
  if (!storedTimestamp) {
    return false;
  }
  return Date.now() - JSON.parse(storedTimestamp) < timeThreshold;
};

/**
 * Fetches data from the given URL and stores it in local storage.
 * Only stores 'timeSeriesData' in the returned response.
 *
 * @param {string} url - The API URL to fetch data from.
 * @param {string} storageKey - The local storage key to store the data under.
 * @param {string} timestampKey - The local storage key to store the timestamp.
 * @returns {Promise<any>} The processed data with 'timeSeriesData'.
 */
const fetchAndStoreData = async (
  url: string,
  storageKey: string,
  timestampKey: string,
): Promise<any> => {
  const response = await fetch(url);
  const data = await response.json();

  // Extract 'timeSeriesData' from 'data'
  const timeSeriesData = data.data;

  // Store 'timeSeriesData' in local storage
  const processedData = { timeSeriesData };
  localStorage.setItem(storageKey, JSON.stringify(processedData));
  localStorage.setItem(timestampKey, JSON.stringify(Date.now()));

  return processedData;
};

const getStorageItem = (key: string): any => {
  const item = localStorage.getItem(key);
  return item ? JSON.parse(item) : null;
};

/**
 * Fetches data from DeFi Llama for a given pool.
 *
 * @param {string} defiLlamaId - The DeFi Llama ID of the pool.
 * @param {boolean} forceFetch - Whether to force fetch from the server.
 * @returns {Promise<{ data: any, isFirstLoadYieldInformationData: boolean }>} A promise that resolves to the data fetched from DeFi Llama and an isFirstLoadYieldInformationData flag.
 * @throws {Error} Throws an error if the API request fails.
 */
export const fetchDefiLlamaYieldData = async (
  defiLlamaId: string,
  forceFetch: boolean = false,
): Promise<{ data: any; isFirstLoadYieldInformationData: boolean }> => {
  const url = `https://yields.llama.fi/poolsEnriched?pool=${defiLlamaId}`;
  const storageKey = generateStorageKey(url, 'yieldData');
  const timestampKey = generateStorageKey(url, 'yieldDataTimestamp');

  logInfo(
    'Fetching DeFi Llama data',
    'DeFiLlamaService.fetchDefiLlamaYieldData',
    { defiLlamaId, forceFetch },
  );

  let isFirstLoadYieldInformationData = true;

  if (
    !forceFetch &&
    isLocalStorageValid(
      timestampKey,
      APP_CONFIG.DEFI_LLAMA_YIELD_DATA_FETCH_INTERVAL_IN_MS,
    )
  ) {
    const storedData = getStorageItem(storageKey);
    if (storedData) {
      logInfo(
        'Using cached DeFi Llama data',
        'DeFiLlamaService.fetchDefiLlamaYieldData',
      );
      isFirstLoadYieldInformationData = false;
      return {
        data: storedData.timeSeriesData[0],
        isFirstLoadYieldInformationData,
      };
    }
  }

  try {
    // Fetch the data and store 'timeSeriesData'
    const data = await fetchAndStoreData(url, storageKey, timestampKey);

    logInfo(
      'Fetched DeFi Llama data successfully',
      'DeFiLlamaService.fetchDefiLlamaYieldData',
      data,
    );

    if (data.timeSeriesData && data.timeSeriesData[0]) {
      return { data: data.timeSeriesData[0], isFirstLoadYieldInformationData };
    }

    logError(
      'Failed to fetch DeFi Llama data: invalid status',
      'DeFiLlamaService.fetchDefiLlamaYieldData',
      data,
    );
    throw new Error('Failed to fetch DeFi Llama data');
  } catch (error) {
    logError(error, 'DeFiLlamaService.fetchDefiLlamaYieldData');
    throw error;
  }
};

/**
 * Fetches historical yield data from DeFi Llama for a given pool.
 * Processes only 'timeSeriesData' before returning.
 *
 * @param {string} defiLlamaId - The DeFi Llama ID of the pool.
 * @returns {Promise<{ timeSeriesData: any[] }>} A promise that resolves to an object containing daily yield data.
 * @throws {Error} Throws an error if the API request fails.
 */
export const fetchDefiLlamaYieldHistoricalData = async (
  defiLlamaId: string,
): Promise<{
  timeSeriesData: any[];
}> => {
  const url = `https://yields.llama.fi/chart/${defiLlamaId}`;

  logInfo(
    'Fetching historical yield data from DeFi Llama',
    'DeFiLlamaService.fetchDefiLlamaYieldHistoricalData',
    { defiLlamaId },
  );

  try {
    // Fetch the data directly from the API
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to fetch data from DeFi Llama: ${response.status} ${response.statusText}`,
      );
    }
    const data = await response.json();

    // Extract only 'timeSeriesData' before returning
    const { data: timeSeriesData } = data;

    logInfo(
      'Fetched historical yield data successfully',
      'DeFiLlamaService.fetchDefiLlamaYieldHistoricalData',
      { defiLlamaId },
    );

    return {
      timeSeriesData,
    };
  } catch (error) {
    logError(error, 'DeFiLlamaService.fetchDefiLlamaYieldHistoricalData');
    throw error;
  }
};

/**
 * Fetches current prices for specified tokens from DeFi Llama.
 *
 * @param {Array<{blockchain: string, address: string}>} tokens - Array of objects containing chain names and token addresses.
 * @param {boolean} forceFetch - Whether to force fetch from the server.
 * @returns {Promise<Array<{blockchain: string, address: string, priceInUsd: number} | null>>} A promise that resolves to an array of objects containing blockchain, address, and priceInUsd, or null if no data is available.
 * @throws {Error} Throws an error if the API request fails.
 */
export const fetchDefiLlamaCurrentTokenPrice = async (
  tokens: Array<{ blockchain: string; address: string }>,
  forceFetch: boolean = false,
): Promise<Array<{
  blockchain: string;
  address: string;
  priceInUsd: number;
}> | null> => {
  // Convert blockchain and address to lowercase
  const lowerCaseTokens = tokens.map((token) => ({
    blockchain: token.blockchain.toLowerCase(),
    address: token.address.toLowerCase(),
  }));

  const coins = lowerCaseTokens
    .map((token) => `${token.blockchain}:${token.address}`)
    .join(',');
  const url = `https://coins.llama.fi/prices/current/${coins}`;
  const storageKey = generateStorageKey(url, 'currentPriceData');
  const timestampKey = generateStorageKey(url, 'currentPriceDataTimestamp');

  logInfo(
    'Fetching current prices from DeFi Llama',
    'DeFiLlamaService.fetchDefiLlamaCurrentTokenPrice',
    { tokens: lowerCaseTokens, forceFetch },
  );
  if (
    !forceFetch &&
    isLocalStorageValid(
      timestampKey,
      APP_CONFIG.DEFI_LLAMA_PRICE_FETCH_INTERVAL_IN_MS,
    )
  ) {
    const storedData = getStorageItem(storageKey);
    if (storedData) {
      logInfo(
        'Using cached current price data',
        'DeFiLlamaService.fetchDefiLlamaCurrentTokenPrice',
      );
      return storedData;
    }
  }

  try {
    const response = await fetch(url);
    const data = await response.json();
    const formattedData = lowerCaseTokens.map((token) => ({
      blockchain: token.blockchain,
      address: token.address,
      priceInUsd:
        data.coins[`${token.blockchain}:${token.address}`]?.price || 0,
    }));

    // Check if all prices are zero
    const allPricesZero = formattedData.every((item) => item.priceInUsd === 0);

    if (allPricesZero) {
      logInfo(
        'All prices fetched are zero, returning null',
        'DeFiLlamaService.fetchDefiLlamaCurrentTokenPrice',
      );
      return null;
    }

    localStorage.setItem(storageKey, JSON.stringify(formattedData));
    localStorage.setItem(timestampKey, JSON.stringify(Date.now()));

    logInfo(
      'Fetched current price data successfully',
      'DeFiLlamaService.fetchDefiLlamaCurrentTokenPrice',
      formattedData,
    );

    return formattedData;
  } catch (error) {
    logError(error, 'DeFiLlamaService.fetchDefiLlamaCurrentTokenPrice');
    return null;
  }
};
